import { apiClient, authService } from "./config";
import {
  TimestampResponse,
  VerificationResponse,
  JobStatus,
  AdminMetrics,
  Timestamp,
} from "../../types/api";

const ensureAuthenticated = async () => {
  if (authService.isAuthenticated()) {
    return;
  }

  try {
    const loginResult = await authService.autoLogin();

    // If auto-login failed and we're still not authenticated, throw a user-friendly error
    if (!loginResult && !authService.isAuthenticated()) {
      // Check if environment variables are set
      const username = process.env.REACT_APP_TIMESTAMP_API_USERNAME;
      const password = process.env.REACT_APP_TIMESTAMP_API_PASSWORD;

      if (!username || !password) {
        throw new Error(
          "Authentication failed: Missing API credentials in environment variables. " +
          "Please set REACT_APP_TIMESTAMP_API_USERNAME and REACT_APP_TIMESTAMP_API_PASSWORD."
        );
      } else {
        throw new Error(
          "Authentication failed: Invalid credentials. Please check your username and password."
        );
      }
    }
  } catch (error) {
    if (error instanceof Error) {
      if (
        error.message.includes("Network Error") ||
        error.message.includes("ERR_NETWORK")
      ) {
        throw new Error(
          "Cannot connect to the API server. Please check your network connection and API URL."
        );
      }

      if (error.message.includes("Invalid credentials")) {
        throw new Error(
          "Authentication failed: The username or password provided is incorrect."
        );
      }

      if (error.message.includes("Server error")) {
        throw new Error(
          "Authentication failed: The API server encountered an error. Please try again later."
        );
      }
    }

    console.error("Authentication error details:", error);
    throw error;
  }
};

export const timestampService = {
  login: async (username?: string, password?: string) => {
    return authService.login(username, password);
  },

  autoLogin: async () => {
    return authService.autoLogin();
  },

  logout: () => {
    authService.logout();
  },

  isAuthenticated: () => {
    return authService.isAuthenticated();
  },

  createTimestamp: async (data: string, hashAlg: string = "SHA256") => {
    await ensureAuthenticated();

    const response = await apiClient.post<TimestampResponse>("/api/timestamp", {
      data,
      hashAlg,
    });

    return response.data;
  },

  verifyTimestamp: async (certificate: string) => {
    const response = await apiClient.post<VerificationResponse>("/api/verify", {
      certificate,
    });
    return response.data;
  },

  getTimestampByHash: async (
    hash: string,
    format?: "base64" | "raw" | null
  ) => {
    await ensureAuthenticated();

    const encodedHash = encodeURIComponent(hash);
    const path = format
      ? `/admin/hash?hash=${encodedHash}&format=${format}`
      : `/admin/hash?hash=${encodedHash}`;
    const response = await apiClient.get<Timestamp>(path);
    return response.data;
  },

  getJobStatus: async (jobId: string) => {
    await ensureAuthenticated();

    const response = await apiClient.get<JobStatus>(`/api/status/${jobId}`);
    return response.data;
  },

  getMetrics: async () => {
    await ensureAuthenticated();

    const response = await apiClient.get<AdminMetrics>(`/admin/metrics`);
    return response.data;
  },

  getTimestamps: async () => {
    await ensureAuthenticated();

    const response = await apiClient.get<Timestamp[]>(`/admin/timestamps`);
    return response.data;
  },
};
